import { listCacheEntries, dropCache } from 'lib/indexedDbCache';
import {
    ACCOUNT_USERS_CACHE_PREFIX,
    ACCOUNT_USERS_RETENTION_MS,
    MAX_CACHED_ACCOUNTS,
} from 'constants/cache/accountUsersCache';

/**
 * Evicts stale and excess account-users buckets from IndexedDB.
 *
 * Two passes: (1) retention — drop buckets not accessed within ACCOUNT_USERS_RETENTION_MS;
 * (2) LRU — if more than MAX_CACHED_ACCOUNTS remain, drop the least-recently-accessed until within
 * the cap. Pairs are immutable, so this is garbage collection of unused/deleted accounts, not freshness.
 *
 * @returns {Promise<void>}
 */
export const evictAccountUsersCache = async () => {
    const now = Date.now();
    const entries = await listCacheEntries();
    const buckets = entries.filter(([key]) => typeof key === 'string' && key.startsWith(ACCOUNT_USERS_CACHE_PREFIX));

    const expired = [];
    const surviving = [];
    buckets.forEach(([key, envelope]) => {
        const lastAccessedAt = envelope?.lastAccessedAt ?? 0;
        if (now - lastAccessedAt > ACCOUNT_USERS_RETENTION_MS) {
            expired.push(key);
        } else {
            surviving.push({ key, lastAccessedAt });
        }
    });

    surviving.sort((a, b) => a.lastAccessedAt - b.lastAccessedAt); // oldest first
    const overflowCount = Math.max(0, surviving.length - MAX_CACHED_ACCOUNTS);
    const overflow = surviving.slice(0, overflowCount).map((bucket) => bucket.key);

    await Promise.all([...expired, ...overflow].map((key) => dropCache(key)));
};
